import { useEffect, useState } from "react";
import { api, mediaUrl } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Loading, PageHero, ApiOffline } from "@/components/public/Ui";
import { ContentSections } from "@/components/public/ContentSections";
import NotFound from "@/pages/public/NotFound";

/** Renders any CMS-managed standard page by slug. */
export default function ContentPage({ slug }) {
  const [page, setPage] = useState(null);
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    setPage(null);
    setOffline(false);
    api
      .get(`/public/pages/${slug}`)
      .then((r) => setPage(r.data))
      .catch((err) => {
        // A 404 means the page genuinely does not exist; anything else (network error,
        // unreachable API) is an infrastructure problem and must not look like a 404.
        if (err?.response?.status === 404) setPage(false);
        else setOffline(true);
      });
  }, [slug]);

  if (offline) return <ApiOffline what="this page" />;
  if (page === null) return <Loading />;
  if (page === false) return <NotFound />;

  const hero = page.body_json?.hero || {};
  const backdrop = page.body_json?.backdrop_image;

  return (
    <>
      <Seo title={page.seo_title || page.title} description={page.seo_description} canonicalPath={`/${slug}`} />
      <PageHero
        overline={hero.overline}
        line1={hero.heading_line_1 || page.title}
        line2={hero.heading_line_2}
        subheading={hero.subheading}
        image={mediaUrl(page.hero_image_url)}
      />
      <div className="relative">
        {backdrop && (
          <img
            src={mediaUrl(backdrop)}
            alt=""
            aria-hidden="true"
            data-testid={`page-${slug}-backdrop`}
            className="pointer-events-none absolute right-0 top-10 hidden w-[46%] max-w-[620px] opacity-[0.22] lg:block"
          />
        )}
        <div className="bka-container relative max-w-4xl py-14 lg:py-20" data-testid={`page-${slug}`}>
          <ContentSections sections={page.body_json?.sections || []} />
        </div>
      </div>
    </>
  );
}
